import jsonata from 'jsonata';
import { useMemo } from 'react';
import jsonataFunctions from './jsonataFunctions';
import { concatenatePaths, getObject, parseRelPath, CUSTOM_PATHS, interpolate, ParsedRelativePath, CustomMapKey, MapKeyCommons, ParsedMapKeyCommons } from './mapObject';
import { Brand } from './types';
import { i18n } from '../i18n';


export const DEFAULT_SCOPE = '$.';
export type DefaultScope = typeof DEFAULT_SCOPE;


export type JnxTextExpr<EvalType=any> = string & Brand<EvalType>
export type JnxObjectExpr<EvalType=any> = {
  expr: JnxTextExpr<EvalType> | JnxTextExpr<EvalType>[],
  scope?: string,
  debug?: boolean | string
};

export type JnxNormalizedObjectExpr<EvalType=any> = {
  expr: JnxTextExpr<EvalType>,
  scope?: string,
  debug?: boolean | string
};

export type JnxExpr<EvalType=any> = (
  JnxTextExpr<EvalType>
  | JnxTextExpr<EvalType>[]
  | JnxNormalizedObjectExpr<EvalType>
  | JnxObjectExpr<EvalType>
);

export type FunctionBinding = (() => any) & {
  signature?: string
}
export type FunctionBindings = {
  [key: string]: FunctionBinding
};

export type Data = any;
type Binding = Data;
export type Bindings = Record<string, Binding>;


type ExprWrapperFunction = (expr: JnxTextExpr) => JnxTextExpr;

export type JnxDelegateFunction<EvalType=any> = (args: any[], jnx: Jnx<EvalType>) => any
export type UseJnxOptions<EvalType=any> = {
  fn?: JnxDelegateFunction<EvalType>
  functionBinds?: FunctionBindings
};


class Jnx<EvalType = any> {
  expression: string
  fn: jsonata.Expression
  scope: ParsedRelativePath | DefaultScope
  debug?: boolean | string
  _fnBindings: FunctionBindings

  constructor(jnx: JnxExpr<EvalType>){
    const objJnx = normalizeToExprObject(jnx);

    this.expression = objJnx.expr;

    try {
      this.fn = jsonata(this.expression);
    } catch (e){
      console.error('parsing expression: ', this.expression);
      throw e;
    }

    const scope = objJnx.scope;
    this.scope = (scope && scope !== DEFAULT_SCOPE) ? parseRelPath(scope) : DEFAULT_SCOPE;
    this.debug = objJnx.debug;

    this.debug = objJnx.debug;
    this._fnBindings = {};

    this.bindFunctions(jsonataFunctions as unknown as FunctionBindings);
  }

  bindFunctions(functionBinds?: FunctionBindings){
    Object.entries(functionBinds || {}).forEach(([name, fn]) => {
      this.fn.registerFunction(name, fn, fn.signature || undefined);
      this._fnBindings[name] = fn;
    });
  }

  eval(data: Data): EvalType;
  eval(data: Data, path: string, bindings: Bindings | null): EvalType;
  eval(data: Data, path: string, bindings: Bindings | null, promised: false | undefined): EvalType;
  eval(data: Data, path: string, bindings: Bindings | null, promised: true): Promise<EvalType>;
  eval(data: Data, path: string='', bindings: Bindings | null = null, promised: boolean = false): EvalType | Promise<EvalType>{
    let context = data;
    const evalBindings = {...(bindings || { })};
    // Exposes the active app locale as `$locale` to every JNX expression (e.g. a
    // lookup's `label` or a column's `valueTranform`), so schema authors can branch
    // display text by language without a separate translation mechanism for computed values.
    if (evalBindings.locale === undefined) {
      evalBindings.locale = i18n.locale;
    }
    evalBindings.path = path;
    evalBindings.rootData = data;

    if(this.scope !== DEFAULT_SCOPE){
      const newPath = concatenatePaths(path, this.scope);
      context = getObject(data, newPath);
      evalBindings.path = newPath;
    }

    evalBindings.evalContext = context;

    if (this.debug) {
      console.debug(`jnx expression debug tag: ${this.debug}`);
      console.debug({expression: this.expression, context, bindings: evalBindings, path, scope: this.scope, fns: this._fnBindings});
    }

    if (promised) {
      return new Promise((resolve, reject) => {
        this.fn.evaluate(context, evalBindings, (error, result) => {
          if (error) {
            if (this.debug) console.debug('error: ', error);
            reject(error);
          } else {
            if (this.debug) console.debug('result: ', result);
            resolve(result);
          }
        });
      });
    } else {
      const result = this.fn.evaluate(context, evalBindings);
      if (this.debug) console.debug('result: ', result);

      return result;
    }
  }

  evalAsync(data: Data, path: string='', bindings: Bindings | null = null){
    return this.eval(data, path, bindings, true);
  }
}

export function normalizeToExprObject<EvalType=any>(jnx?: undefined, defaultScope?: string): JnxNormalizedObjectExpr<undefined>;
export function normalizeToExprObject<EvalType=any>(jnx: JnxExpr<EvalType>, defaultScope?: string): JnxNormalizedObjectExpr<EvalType>;
export function normalizeToExprObject<EvalType=any>(jnx?: JnxExpr<EvalType>, defaultScope: string = DEFAULT_SCOPE): JnxNormalizedObjectExpr<EvalType> | JnxNormalizedObjectExpr<undefined> {
  if (Array.isArray(jnx)){
    jnx = jnx.join('\n') as JnxTextExpr<EvalType>;
  }

  if (typeof jnx === 'string') {
      const m = /^(.*?)(\!<scope>:([^\!]*?))?(\!<tag>:(.*?))?$/.exec(jnx);
      if (m) {
        return { expr: m[1] as JnxTextExpr<EvalType>, scope: m[3] ?? defaultScope, debug: m[5] };
      } else {
        jnx = { expr: jnx, scope: defaultScope };
      }
  } else if (typeof jnx === 'number' || typeof jnx === 'boolean'){
    jnx = { expr: `${jnx}` as JnxTextExpr<EvalType>, scope: defaultScope };
  } else if (jnx === undefined) {
    return { expr: "" as JnxTextExpr<EvalType>, scope: defaultScope};
  }

  jnx.expr = (Array.isArray(jnx.expr) ? jnx.expr.join('\n') : `${jnx.expr}`) as JnxTextExpr<EvalType>;

  return jnx as JnxNormalizedObjectExpr<EvalType>;
}

export function parseJnxExpr<EvalType=any>(jnxExpr: JnxExpr<EvalType>, defaultScope=DEFAULT_SCOPE){
  let jnx = jnxExpr;

  if (Array.isArray(jnx) || typeof jnx === 'string' || typeof jnx === 'number' || typeof jnx === 'boolean'){
    jnx = { expr: jnx, scope: defaultScope };
  }

  if (jnx.scope === undefined) {
    jnx.scope = defaultScope;
  }

  jnx.expr = (Array.isArray(jnx.expr) ? jnx.expr.join('\n') : `${jnx.expr}`) as JnxTextExpr<EvalType>;

  return jnx;
}

export function embedJnx<EvalType=any>(expr: JnxTextExpr<EvalType>, ...wrapperFns: ExprWrapperFunction[]) {
  const jnxObj = normalizeToExprObject(expr);
  wrapperFns.forEach(wrapperFn => {
    jnxObj.expr = wrapperFn(jnxObj.expr);
  });

  return jnxObj;
}

export function useJnx<EvalType=any>(
  expression?: null | undefined,
  options?: (UseJnxOptions<EvalType> & {fn: JnxDelegateFunction<EvalType>}) | JnxDelegateFunction<EvalType>,
  fndeps?: React.DependencyList
): null;
export function useJnx<EvalType=any>(
  expression?: null | undefined | JnxExpr<EvalType>,
  options?: (UseJnxOptions<EvalType> & {fn: JnxDelegateFunction<EvalType>}) | JnxDelegateFunction<EvalType>,
  fndeps?: React.DependencyList
): Jnx<EvalType> | null;
export function useJnx<EvalType=any>(
  expression: JnxExpr<EvalType> | null | undefined,
  options?: UseJnxOptions<EvalType> & ({fn: undefined} | {}),
  fndeps?: React.DependencyList
): Jnx<EvalType>;
export function useJnx<EvalType=any>(
  expression: JnxExpr<EvalType> | null | undefined,
  options?: (UseJnxOptions<EvalType> & {fn: JnxDelegateFunction<EvalType>}) | JnxDelegateFunction<EvalType>,
  fndeps?: React.DependencyList
): ((...args: any[]) => any);
export function useJnx<EvalType=any>(
  expression?: JnxExpr<EvalType> | null | undefined,
  options?: UseJnxOptions<EvalType> | JnxDelegateFunction<EvalType>,
  fndeps?: React.DependencyList
){
  const {
    fn,
    functionBinds
  } = options && (typeof(options) === 'function') ? {fn : options} : (options || {});

  const stableFn = useMemo(() => fn, [...(fndeps || [])]);

  return useMemo(() => {
    try {
      const jnx = expression ? new Jnx(expression) : null;

      if (jnx) {
        jnx.bindFunctions(functionBinds);
      }

      return stableFn && jnx ? (...args: any[]) => stableFn(args, jnx) : jnx;
    } catch (e){
      console.error(e);

      return null;
    }
  }, [expression, stableFn, functionBinds]);
}

export type MultipleJnxObject<EvalType=any> = {
  [k in string]: (
    k extends `expr:${string}` ? JnxExpr<EvalType> : any
  )
}

export function useMultipleJnx<EvalType=any>(
  expressions?: MultipleJnxObject<EvalType>,
  options?: { functionBinds?: FunctionBindings, fn: undefined},
  fndeps?: React.DependencyList
): Record<string, Jnx<EvalType> | null>;
export function useMultipleJnx<EvalType=any>(
  expressions?: MultipleJnxObject<EvalType>,
  options?: { functionBinds?: FunctionBindings, fn: JnxDelegateFunction<EvalType>} | JnxDelegateFunction<EvalType>,
  fndeps?: React.DependencyList
): Record<string, ((...args: any[]) => any) | null>;
export function useMultipleJnx<EvalType=any>(expressions?: MultipleJnxObject<EvalType>, options?: UseJnxOptions | JnxDelegateFunction<EvalType>, fndeps?: React.DependencyList) {
  const {
    fn,
    functionBinds
  } = options && (typeof (options) === 'function') ? { fn: options } : (options || {});

  const stableFn = useMemo(() => fn, [...(fndeps || [])]);

  if (!expressions)
    return null;

  return useMemo(() => {
    try {
      const result: Record<string, JnxDelegateFunction<EvalType> | Jnx<EvalType> | null> = {};
      Object.entries(expressions).forEach(([key, expression]) => {
        if (!key.toString().startsWith('expr:'))
          return;
        const jnx = expression ? new Jnx(expression as JnxExpr<EvalType>) : null;

        if (jnx) {
          jnx.bindFunctions(functionBinds);
        }

        result[key.replace(/^expr:/, '')] = stableFn && jnx ? (...args) => stableFn(args, jnx) : jnx;
      });

      return result;
    } catch (e) {
      console.error(e);

      return null;
    }
  }, [expressions, stableFn, functionBinds]);
}


export class InterpolatedJnxTemplate {
  parsed: (string | Jnx)[]

  constructor(template: string, defaultScope?: string) {
    this.parsed = InterpolatedJnxTemplate.__parseTemplate(template, defaultScope);
  }

  bindFunctions(functionBinds?: FunctionBindings){
    this.parsed.forEach(item => {
      if (item instanceof Jnx) item.bindFunctions(functionBinds)
    });
  }

  /** True if the template has at least one `$expr`/`${expr}` interpolation to evaluate. */
  hasInterpolation(): boolean {
    return this.parsed.some(item => item instanceof Jnx);
  }

  eval(data: Data, path: string, bindings: Bindings | null, promised: true): Promise<string>;
  eval(data: Data, path: string, bindings: Bindings | null, promised: false): string;
  eval(data: Data, path: string='', bindings: Bindings | null = null, promised: boolean = false): string | Promise<string>{
    if (promised) return this.evalAsync(data, path, bindings);
    return this.parsed.map(item => (item instanceof Jnx) ? item.eval(data, path, bindings) : item).join('');
  }

  async evalAsync(data: Data, path: string='', bindings: Bindings | null = null){
    return (await Promise.all(this.parsed.map(item =>
      (item instanceof Jnx)
      ? item.evalAsync(data, path, bindings)
      : item
    ))).join('');
  }

  static __parseTemplate(template: string, defaultScope?: string) {
    const regex = /\$(([a-zA-Z]+)|{([^}]+)})/g;
    const parsed: (string | Jnx)[] = [];

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(template)) !== null) {
      const matchStart = match.index;
      const matchEnd = regex.lastIndex;

      const literal = template.slice(lastIndex, matchStart);
      const interpolation = match[2] || match[3]; // match[2] = $name, match[3] = ${count}
      let jnx = null
      try {
        const exprObj = normalizeToExprObject(interpolation as JnxTextExpr<string>, defaultScope);
        jnx = new Jnx(exprObj)
      } catch (e) {
        console.error(e);
        jnx = `[syntax-error]`
      }

      parsed.push(literal, jnx);
      lastIndex = matchEnd;
    }

    // Add any trailing text after the last match
    if (lastIndex < template.length) {
      parsed.push(template.slice(lastIndex));
    }

    return parsed;
  }
};


type JnxMapKey = MapKeyCommons & {expr: JnxExpr};
type ParsedJnxMapKey = ParsedMapKeyCommons & {expr: Jnx};
CUSTOM_PATHS.expr = {
  parse(mapKey){
    const jnxMapKey = mapKey as JnxMapKey;
    return { type: 'expr', expr: new Jnx(jnxMapKey.expr) };
  },
  resolve(pv, pk, object, initialObject, context){
    const pJnxMapKey = pv as ParsedJnxMapKey;
    return pJnxMapKey.expr.eval(object, '', {
      pv,
      pk,
      context,
      initialObject
    });
  }
};


export default Jnx;